import React from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/authContext";
import { useTheme } from "../context/themeContext";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiUser, FiSettings, FiLogOut, FiMoon, FiSun } from "react-icons/fi";

const MobileMenu = ({ isOpen, onClose }) => {
  const { auth, handleLogout } = React.useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/projects", label: "Projects" },
    { path: "/blogs", label: "Blogs" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  const handleLinkClick = () => {
    onClose();
  };

  const handleLogoutClick = () => {
    handleLogout();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={onClose}
          />

          {/* Menu */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 120 }}
            className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white dark:bg-gradient-to-b dark:from-[#2a2a2a] dark:to-[#1a1a1a] shadow-2xl z-50 lg:hidden border-l border-gray-200 dark:border-brand-purple/20"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-brand-purple/20">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">Menu</h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-800 dark:text-white hover:text-brand-gold dark:hover:text-brand-gold transition-colors duration-300 rounded-lg hover:bg-brand-purple/10"
                aria-label="Close menu"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            {/* Navigation Items */}
            <nav className="flex flex-col py-4">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 + 0.1 }}
                >
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    aria-current={
                      location.pathname === item.path ? "page" : undefined
                    }
                    className={`flex items-center px-6 py-4 text-lg font-semibold transition-all duration-300 border-l-4 ${
                      location.pathname === item.path
                        ? "text-brand-gold bg-brand-purple/10 border-brand-gold"
                        : "text-gray-800 dark:text-white hover:text-brand-gold dark:hover:text-brand-gold hover:bg-brand-purple/5 border-transparent"
                    }`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}

              {/* Separator */}
              <div className="mx-6 my-4 h-px bg-gray-200 dark:bg-brand-purple/20" />

              {/* Theme Toggle */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <button
                  onClick={toggleTheme}
                  className="flex items-center px-6 py-4 text-lg font-semibold text-gray-800 dark:text-white hover:text-brand-gold dark:hover:text-brand-gold hover:bg-brand-purple/5 transition-all duration-300 w-full text-left border-l-4 border-transparent"
                >
                  {theme === "dark" ? (
                    <>
                      <FiSun className="mr-3 text-xl" />
                      Light Mode
                    </>
                  ) : (
                    <>
                      <FiMoon className="mr-3 text-xl" />
                      Dark Mode
                    </>
                  )}
                </button>
              </motion.div>

              {/* Separator */}
              <div className="mx-6 my-4 h-px bg-gray-200 dark:bg-brand-purple/20" />

              {/* Auth Section */}
              {!auth.isAuthenticated ? (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <Link
                    to="/login"
                    onClick={handleLinkClick}
                    className={`flex items-center px-6 py-4 text-lg font-semibold transition-all duration-300 border-l-4 ${
                      location.pathname === "/login"
                        ? "text-brand-gold bg-brand-purple/10 border-brand-gold"
                        : "text-gray-800 dark:text-white hover:text-brand-gold dark:hover:text-brand-gold hover:bg-brand-purple/5 border-transparent"
                    } group`}
                  >
                    <FiUser className="mr-3 text-xl" />
                    Admin Access
                    <span className="ml-2 w-2 h-2 bg-gradient-to-r from-brand-gold to-brand-purple rounded-full opacity-60 group-hover:opacity-100 transition-opacity"></span>
                  </Link>
                </motion.div>
              ) : (
                <>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Link
                      to="/admin-dashboard"
                      onClick={handleLinkClick}
                      className={`flex items-center px-6 py-4 text-lg font-semibold transition-all duration-300 border-l-4 ${
                        location.pathname === "/admin-dashboard"
                          ? "text-brand-gold bg-brand-purple/10 border-brand-gold"
                          : "text-gray-800 dark:text-white hover:text-brand-gold dark:hover:text-brand-gold hover:bg-brand-purple/5 border-transparent"
                      } group`}
                    >
                      <FiSettings className="mr-3 text-xl" />
                      Dashboard
                      <span className="ml-2 w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    </Link>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    <button
                      onClick={handleLogoutClick}
                      className="flex items-center px-6 py-4 text-lg font-semibold text-red-600 dark:text-red-300 hover:text-red-700 dark:hover:text-red-200 hover:bg-red-500/10 transition-all duration-300 w-full text-left border-l-4 border-transparent hover:border-red-400/30"
                    >
                      <FiLogOut className="mr-3 text-xl" />
                      Logout
                    </button>
                  </motion.div>
                </>
              )}
            </nav>

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-gray-200 dark:border-brand-purple/20">
              <div className="text-center">
                <p className="text-gray-500 dark:text-white/60 text-sm">
                  Portfolio by David Seibold
                </p>
                <p className="text-brand-gold text-xs mt-1 font-mono">
                  San Diego, CA
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileMenu;
